/* components/patterns/charts/Charts.jsx — recharts-эквивалент для прототипа (S8).
   HBarChart      — горизонтальный bar (клик → drill-down), числа в Mono.
   LineChartMulti — многолинейный график (измеряемый SVG, ResponsiveContainer-аналог),
                    кликабельная легенда (toggle серии), hover-tooltip с вертикальной
                    направляющей. Палитра серий — токены --c-series-1..10 (сдержанная;
                    первые шесть — в прежнем порядке, 7–10 добавлены под 7+ категорий).
   ChartEmpty     — пустое состояние графика.
   Данные приходят только пропсами (без импорта моков). Числа форматируются через lib/format.

   ХЕНДОФФ → frontend/components/finance/charts/* на recharts. Контракт переноса (P1-28),
   чтобы модель данных и Tooltip/Legend совпали 1:1:
   • LineChartMulti: series[{id,name,points:[{date,value}]}] → плоский data[] по датам
     ({date, [series.id]: value, ...}); по серии <Line dataKey={s.id} stroke="var(--c-series-N)" />.
     Tooltip — <Tooltip content={...}> рендерит те же lc-tip строки (vf=lib/format); оси —
     <YAxis tickFormatter={af} /> / <XAxis tickFormatter={fmt.dateShort} />, шрифт var(--fs-10).
   • HBarChart: data[{id,label,value,tone}] → <BarChart layout="vertical" data> + <Bar onClick={onPick}>;
     tone → fill="var(--status-*)" иначе color; подпись значения — <LabelList formatter={vf}>.
   • Legend — <Legend content={...}> с toggle серии (как lc-leg). Палитра — токены --c-series-1..10. */

import React from 'react';
import {
  Area, Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line, LineChart, Pie, PieChart,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Icon } from '../../ui/Icon.jsx';
import { Button } from '../../ui/Button.jsx';
import { fmt } from '../../../lib/format.js';
import { EXPAND_LIMIT, ExpandableList } from '../ExpandableList.jsx';
import { ChartLegend } from './ChartLegend.jsx';

  const { useEffect, useRef, useState } = React;

  /* Фокус клавиатуры после «Скрыть все / Показать все». Нажатая кнопка в этот момент
     выключается («Скрыть все», когда скрыто всё; «Показать все» в легенде, когда всё
     показано) или исчезает вместе с пустым состоянием — и фокус падал на body:
     человек с клавиатуры терял место и начинал обход страницы сначала. Ведём фокус
     явно и после перерисовки (цель к этому моменту уже в DOM и включена): скрыли
     всё → «Показать все» в пустом состоянии; показали всё → «Скрыть все» в легенде
     (кнопок «все» нет — первый чип). После мыши рамка не появится: глобальный
     `:focus-visible` наследует способ, которым фокус пришёл. */
  function useLegendFocus(hidden) {
    const emptyRef = useRef(null);     // «Показать все» в пустом состоянии
    const legendRef = useRef(null);    // «Скрыть все» в легенде / первый чип
    const pending = useRef(null);      // 'empty' | 'legend' — куда вести после перерисовки
    useEffect(() => {
      const to = pending.current;
      pending.current = null;
      const el = to === 'empty' ? emptyRef.current : (to === 'legend' ? legendRef.current : null);
      if (el && typeof el.focus === 'function') el.focus();
    }, [hidden]);
    return { emptyRef, legendRef, pending };
  }

  /* Легенда-переключатель серий — одна на оба картезианских графика (ChartLegend).
     Состояние «что скрыто» держит график; здесь — действия над ним. «Показать все»
     сбрасывает карту целиком, «Скрыть все» — гасит ТЕКУЩИЕ серии (у графика с
     переключаемой метрикой в карте могут лежать id прошлой метрики). */
  function legendActions(series, setHidden, focus) {
    return {
      onToggle: (id) => setHidden((h) => ({ ...h, [id]: !h[id] })),
      onHideAll: () => {
        focus.pending.current = 'empty';
        setHidden((h) => {
          const next = { ...h };
          series.forEach((s) => { next[s.id] = true; });
          return next;
        });
      },
      onShowAll: () => {
        focus.pending.current = 'legend';
        setHidden({});
      },
    };
  }

  /* Все серии скрыты — вместо пустых осей говорим об этом прямо и даём вернуть.
     Пустые оси с сеткой читались как «данных нет», хотя их просто выключили.
     Встаёт в ту же коробку `.linechart__plot`: высота блока не прыгает. */
  function allHiddenState(onShowAll, ref) {
    return (
      <ChartEmpty icon="eye-off" label="Все элементы графика скрыты"
        action={<Button ref={ref} variant="secondary" size="md" iconLeft="eye" onClick={onShowAll}>Показать все</Button>} />
    );
  }

  /* Цвет серии: явный `color` → закреплённый за серией номер палитры `colorIndex` →
     порядковый номер. `colorIndex` нужен графикам, у которых набор и порядок серий
     меняются переключателем (категории у «Позиций» и «Выручки» отсортированы каждая
     по своей сумме): цвет по порядку «гулял» бы вместе с именем. Палитру по-прежнему
     резолвит сам график — тема учитывается (см. комментарий в конце файла). */
  function seriesColors(series, palette) {
    const out = {};
    series.forEach((s, i) => {
      out[s.id] = s.color || palette[(s.colorIndex != null ? s.colorIndex : i) % palette.length];
    });
    return out;
  }

  // Палитра серий из токенов «Линотипа». Recharts красит SVG-атрибутами (var() в них НЕ
  // работает) → резолвим CSS-переменные в реальные цвета на рендере (тема учитывается).
  function cssVar(name, fallback) {
    if (typeof window === 'undefined' || typeof document === 'undefined') return fallback;
    const v = getComputedStyle(document.documentElement).getPropertyValue(name);
    return (v && v.trim()) || fallback;
  }
  /* Десять цветов, а не шесть: у кассы бывает семь и больше категорий, и седьмая по
     кругу брала цвет первой — в стеке «Производительности» и донате «Структуры
     выручки» «Напитки» и «Супы» стояли одним синим. Порядок первых шести прежний:
     цвет серии берётся по номеру, перестановка перекрасила бы все графики.
     Одиннадцатая и дальше по-прежнему идут по кругу. */
  const SERIES_TOKENS = ['--c-series-1', '--c-series-2', '--c-series-3', '--c-series-4', '--c-series-5',
    '--c-series-6', '--c-series-7', '--c-series-8', '--c-series-9', '--c-series-10'];
  const SERIES_FALLBACK = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6',
    '#a9517a', '#4a7619', '#1697c5', '#855a47'];
  const seriesPalette = () => SERIES_TOKENS.map((t, i) => cssVar(t, SERIES_FALLBACK[i]));

  /* ── HBarChart ── data:[{id,label,value,tone?}] ── */
  function HBarChart({ data = [], unit = '₽', onPick, max, color = 'var(--accent)' }) {
    if (!data.length) return <ChartEmpty />;
    const hi = max || Math.max(1, ...data.map((d) => d.value));
    return (
      <div className="hbar">
        {data.map((d) => {
          const pct = Math.max(2, Math.round((d.value / hi) * 100));
          const inner = (
            <>
              <span className="hbar__label truncate" title={d.label}>{d.label}</span>
              <span className="hbar__track">
                <span className="hbar__fill" style={{ width: pct + '%', background: d.tone ? 'var(--status-' + d.tone + ')' : color }} />
              </span>
              <span className="hbar__val num">{fmt.money ? (unit === '₽' ? fmt.money(d.value) : d.value + ' ' + unit) : d.value}</span>
            </>
          );
          return onPick
            ? <button type="button" key={d.id} className="hbar__row hbar__row--click" onClick={() => onPick(d)}>{inner}</button>
            : <div key={d.id} className="hbar__row">{inner}</div>;
        })}
      </div>
    );
  }

  // Унифицированные пропсы тултипа recharts: позиционирует САМ recharts (контент без
  // своего position!), может выходить за тесный viewBox, поверх остального. Один источник
  // правды для всех картезианских чартов — чтобы не ловить «прибито к углу/обрезано».
  const CHART_TOOLTIP_PROPS = {
    allowEscapeViewBox: { x: true, y: true },
    wrapperStyle: { zIndex: 30, outline: 'none' },
  };

  /* ── Tooltip для LineChartMulti (унифицированный .chart-tip) ── */
  function LcTip({ active, payload, label, vf }) {
    if (!active || !payload || !payload.length) return null;
    /* `tooltipType="none"` у элемента графика recharts кладёт в запись payload
       `type: 'none'`, но отфильтровывает такие записи ТОЛЬКО встроенная
       подсказка; своей разметке это нужно делать самой. Без этого заливка
       зоны серии (режим вокруг нуля) дублировала каждую строку подсказки.
       Заодно не показываем одну серию дважды, чем бы дубль ни был вызван. */
    const seen = new Set();
    const rows = payload.filter((p) => {
      if (p.value == null || p.type === 'none') return false;
      const key = p.dataKey != null ? String(p.dataKey) : p.name;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (!rows.length) return null;
    return (
      <div className="chart-tip">
        <div className="chart-tip__date">{fmt.date(label)}</div>
        {rows.map((p) => (
          <div className="chart-tip__row" key={p.dataKey}>
            <span className="chart-tip__dot" style={{ background: p.color || p.stroke }} />
            <span className="chart-tip__name truncate">{p.name}</span>
            <span className="chart-tip__val num">{vf(p.value)}</span>
          </div>
        ))}
      </div>
    );
  }

  /* «Красивый» потолок для симметричной оси: 1 350 000 → 1 500 000, 83 → 100.
     Иначе деления читаются как «1,35M / 675K», и ноль посередине — единственная
     круглая отметка на оси. */
  function niceCeil(m) {
    if (!(m > 0)) return 1;
    const p = 10 ** Math.floor(Math.log10(m));
    const f = m / p;
    const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
    return nf * p;
  }

  /* ── LineChartMulti (Recharts) ── series:[{id,name,points:[{date,value}],color?,colorIndex?,dashed?}] ──
     onPickDate(date) — опц.: клик по графику → дата (drill-down). Цвета серий и осей —
     из токенов «Линотипа»; легенда-toggle сохранена; `dashed` рисует пунктир (окно сравнения).
     xFormat(isoDate) — опц.: своя подпись оси X.

     zeroCentered — ОСЬ ВОКРУГ НУЛЯ: домен симметричный [−m, m] по большему модулю,
     ноль ровно посередине и подчёркнут линией, под каждой серией — заливка до
     нуля. Для рядов, где знак и есть смысл (излишки вверх, недостачи вниз):
     на обычной оси «от нуля вверх» обе серии лежали в одной положительной зоне,
     и что одна из них — потеря, а другая — приход, график не говорил.

     tooltipEscapeX — по умолчанию false: подсказка зеркалится внутри области графика.
     С выходом по X подсказка последних точек уезжала за правый край окна, и страница
     прокручивалась вбок, пока курсор на графике (тренды «Продаж», «Персонала»,
     «Инвентаризации»: +98 px на 1740, +151 px на 380). Включать выход — только
     осознанно, для графика, у которого справа гарантированно есть место. */
  function LineChartMulti({ series = [], unit = '₽', valueFormat, axisFormat, xFormat,
    lineType = 'monotone', allowDecimals = true, tooltipEscapeX = false, onPickDate,
    zeroCentered = false, markX = null }) {
    const [hidden, setHidden] = useState({});
    const focus = useLegendFocus(hidden);
    const vf = valueFormat || ((v) => fmt.money(v));
    const af = axisFormat || vf;
    // Подпись оси X по умолчанию — прежний fmt.dateShort (ряд по дням). Для недельной или
    // месячной гранулярности ключ бакета — начало периода, и «01.09» соврало бы про весь
    // период: такому потребителю нужна своя подпись («нед. 01.09», «сентябрь»).
    const xf = xFormat || fmt.dateShort;
    // `lineType` и `allowDecimals` — опциональные, дефолты сохраняют прежнее
    // поведение всех существующих потребителей до пикселя.
    //
    // Зачем они понадобились: для СЧЁТЧИКОВ (переходы, регистрации, оплаты)
    // сплайн `monotone` рисует плавный подъём там, где было одно событие, —
    // читается как постепенный рост, которого не было. А десятичные деления
    // оси дают «0,75 перехода»: величина, которой не бывает. Денежным рядам
    // и то, и другое подходит, поэтому переключаем адресно, а не глобально.
    if (!series.length) return <div className="linechart"><div className="linechart__plot" /><ChartEmpty /></div>;

    const colorById = seriesColors(series, seriesPalette());

    // общий X — объединение дат всех серий; data[] = строка на дату со столбцами по id серий
    const dateSet = new Set();
    series.forEach((s) => (s.points || []).forEach((p) => { if (p && p.date != null) dateSet.add(p.date); }));
    const dates = Array.from(dateSet).sort();
    const byDate = {};
    dates.forEach((d) => { byDate[d] = { date: d }; });
    series.forEach((s) => (s.points || []).forEach((p) => {
      if (p && p.date != null && p.value != null && Number.isFinite(p.value)) byDate[p.date][s.id] = p.value;
    }));
    const data = dates.map((d) => byDate[d]);

    const axisColor = cssVar('--text-mute', '#8a8f98');
    const gridColor = cssVar('--border-faint', '#eceef1');
    const onClick = onPickDate ? (st) => { if (st && st.activeLabel != null) onPickDate(st.activeLabel); } : undefined;

    const shownSeries = series.filter((s) => !hidden[s.id]);
    const legend = legendActions(series, setHidden, focus);
    const legendSeries = series.map((s) => ({ id: s.id, name: s.name, color: colorById[s.id] }));
    // Симметричный домен считаем по ПОКАЗАННЫМ сериям: выключенная в легенде
    // серия не должна держать ось растянутой под свои значения.
    let yDomain;
    if (zeroCentered) {
      let m = 0;
      shownSeries.forEach((s) => (s.points || []).forEach((p) => {
        if (p && Number.isFinite(p.value)) m = Math.max(m, Math.abs(p.value));
      }));
      const top = niceCeil(m);
      yDomain = [-top, top];
    }
    const Chart = zeroCentered ? ComposedChart : LineChart;

    return (
      <div className={'linechart linechart--rc' + (zeroCentered ? ' linechart--zero' : '')}>
        <div className="linechart__plot">
          {shownSeries.length === 0 ? allHiddenState(legend.onShowAll, focus.emptyRef) : (
            <ResponsiveContainer width="100%" height="100%">
              <Chart data={data} margin={{ top: 10, right: 18, bottom: 4, left: 4 }}
                onClick={onClick} style={onPickDate ? { cursor: 'pointer' } : undefined}>
                <CartesianGrid vertical={false} stroke={gridColor} />
                {/* markX — отметить точку X (панель товара «Инвентаризации»: месяц,
                    по ячейке которого открыли панель): штриховая вертикаль, чтобы
                    один и тот же график читался в контексте выбранного месяца. */}
                {markX != null && (
                  <ReferenceLine x={markX} stroke={cssVar('--accent', '#3776fe')}
                    strokeDasharray="4 3" strokeWidth={1.5} ifOverflow="extendDomain" />
                )}
                <XAxis dataKey="date" tickFormatter={xf} tickLine={false}
                  axisLine={{ stroke: gridColor }} minTickGap={24}
                  tick={{ fontSize: 10, fill: axisColor }} />
                <YAxis tickFormatter={af} tickLine={false} axisLine={false} width={58}
                  allowDecimals={allowDecimals} domain={yDomain} tickCount={5}
                  tick={{ fontSize: 10, fill: axisColor }} />
                {zeroCentered && (
                  /* Ноль — опорная линия, темнее сетки: это граница между
                     «пришло» и «потеряли», а не просто одно из делений. */
                  <ReferenceLine y={0} stroke={cssVar('--border-strong', '#c9ced6')} strokeWidth={1.5} />
                )}
                {zeroCentered && shownSeries.map((s) => (
                  /* Заливка от линии до нуля — зона серии: положительная над нулём,
                     отрицательная под. В подсказку и легенду не попадает
                     (tooltipType="none"), значения несёт линия. */
                  <Area key={'zone-' + s.id} type={lineType} dataKey={s.id} name={s.name}
                    stroke="none" fill={colorById[s.id]} fillOpacity={0.14} baseValue={0}
                    dot={false} activeDot={false} isAnimationActive={false} connectNulls={false}
                    tooltipType="none" legendType="none" />
                ))}
                {/* `tooltipEscapeX={false}` заставляет recharts ОТЗЕРКАЛИТЬ подсказку у
                    правого края вместо того, чтобы вывести её за пределы области. С
                    разрешённым выходом по X подсказка последней точки уезжала за край
                    окна (замер: правый край 1797 px при ширине окна 1740) — на узком
                    экране это половина её содержимого. */}
                <Tooltip content={(p) => <LcTip {...p} vf={vf} />} {...CHART_TOOLTIP_PROPS}
                  allowEscapeViewBox={{ x: tooltipEscapeX, y: true }}
                  cursor={{ stroke: cssVar('--border-strong', '#c9ced6'), strokeDasharray: '3 3' }} />
                {shownSeries.map((s) => (
                  <Line key={s.id} type={lineType} dataKey={s.id} name={s.name}
                    stroke={colorById[s.id]} strokeWidth={2}
                    strokeDasharray={s.dashed ? '5 4' : undefined}
                    dot={false} activeDot={{ r: 4 }} isAnimationActive={false} connectNulls={false} />
                ))}
              </Chart>
            </ResponsiveContainer>
          )}
        </div>
        <ChartLegend series={legendSeries} hidden={hidden} {...legend} focusRef={focus.legendRef} />
      </div>
    );
  }

  /* ── Tooltip для StackedBarChart: X (час/дата) + «Всего» + разбивка по сериям ──
     «Всего» — только когда на графике больше одной серии: у единственной серии
     (режим «Общее» — она и называется «Всего») итог повторял бы её же строку.

     Строк серий — не больше TIP_MAX (владелец, 06.10): у заведений с десятками категорий
     подсказка была выше окна, страница росла вниз, и при движении мыши подсказка прыгала.
     Больше TIP_MAX серий со значением → TIP_MAX − 1 крупнейших по значению в ЭТОМ столбце
     и строка «ещё N · сумма остальных»; наведённая серия (activeKey) — всегда в списке,
     при необходимости вместо последней строки топа (`tipRows`). */
  const TIP_MAX = 8;
  const SERIES_UNIT = ['категория', 'категории', 'категорий'];
  const pluralRu = (n, one, few, many) => {
    const a = Math.abs(Math.round(Number(n) || 0)); const m10 = a % 10; const m100 = a % 100;
    if (m10 === 1 && m100 !== 11) return one;
    if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
    return many;
  };
  function tipRows(rows, activeKey = null, max = TIP_MAX) {
    if (!(rows.length > max)) return { shown: rows, rest: [] };
    const sorted = rows.slice().sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
    let shown = sorted.slice(0, max - 1);
    if (activeKey != null && !shown.some((p) => p.dataKey === activeKey)) {
      const cur = rows.find((p) => p.dataKey === activeKey);
      if (cur) shown = shown.slice(0, max - 2).concat(cur);
    }
    const keys = new Set(shown.map((p) => p.dataKey));
    return { shown, rest: rows.filter((p) => !keys.has(p.dataKey)) };
  }
  function StackTip({ active, payload, label, vf, lf, activeKey = null, unit = SERIES_UNIT }) {
    if (!active || !payload || !payload.length) return null;
    const rows = payload.filter((p) => p.value != null && p.value !== 0);
    if (!rows.length) return null;
    const total = rows.reduce((s, p) => s + (p.value || 0), 0);
    const { shown, rest } = tipRows(rows, activeKey);
    const restSum = rest.reduce((s, p) => s + (p.value || 0), 0);
    return (
      <div className="chart-tip">
        <div className="chart-tip__date">{lf ? lf(label) : label}</div>
        {payload.length > 1 && (
          <div className="chart-tip__row">
            <span className="chart-tip__name chart-tip__name--total">Всего</span>
            <span className="chart-tip__val num">{vf(total)}</span>
          </div>
        )}
        {shown.map((p) => (
          <div className={'chart-tip__row' + (activeKey != null && p.dataKey === activeKey ? ' is-current' : '')} key={p.dataKey}>
            <span className="chart-tip__dot" style={{ background: p.color || p.fill }} />
            <span className="chart-tip__name truncate">{p.name}</span>
            <span className="chart-tip__val num">{vf(p.value)}</span>
          </div>
        ))}
        {rest.length > 0 && (
          <div className="chart-tip__row chart-tip__row--rest">
            <span className="chart-tip__dot chart-tip__dot--rest" />
            <span className="chart-tip__name">ещё {rest.length} {pluralRu(rest.length, unit[0], unit[1], unit[2])}</span>
            <span className="chart-tip__val num">{vf(restSum)}</span>
          </div>
        )}
      </div>
    );
  }

  /* ── StackedBarChart (Recharts) ── hours:[10..23] ИЛИ dates:['YYYY-MM-DD'] ·
     series:[{id,name,values:[по X],color?,colorIndex?}] ── Вертикальный СТЕК-бар (нагрузка
     по часам «Производительности», закупки по дням): один stackId, легенда-toggle, тултип с
     итогом. dates-режим форматирует ось fmt.dateShort и тултип fmt.date.
     Переиспользует обёртку .linechart (высота plot/легенда).

     tooltipEscapeX — по умолчанию false: подсказка ЗЕРКАЛИТСЯ внутри области графика,
     а не уходит вправо за неё. Столбики стоят до правого края, и подсказка последнего
     часа (23:00) уезжала за окно и на 1740 px, а на 380 px — уже от середины графика:
     страница получала горизонтальную прокрутку на время наведения. С деньгами
     («Выручка» по часам) подсказка шире, и выход становится постоянным. Тот же
     дефект и то же лечение, что у LineChartMulti (см. там). */
  function StackedBarChart({ hours = [], dates = [], series = [], valueFormat, axisFormat, tooltipEscapeX = false,
                           seriesUnit = SERIES_UNIT }) {
    const [hidden, setHidden] = useState({});
    const [activeKey, setActiveKey] = useState(null);   // наведённая серия — для подсказки
    const focus = useLegendFocus(hidden);
    const byDates = dates.length > 0;
    const vf = valueFormat || ((v) => new Intl.NumberFormat('ru-RU').format(Number(v) || 0));
    const af = axisFormat || vf;
    if ((!hours.length && !dates.length) || !series.length) return <div className="linechart linechart--rc"><div className="linechart__plot" /><ChartEmpty /></div>;

    const colorById = seriesColors(series, seriesPalette());
    const xs = byDates ? dates : hours.map((h) => String(h).padStart(2, '0') + ':00');
    const data = xs.map((x, idx) => {
      const row = { x };
      series.forEach((s) => { row[s.id] = (s.values || [])[idx]; });
      return row;
    });
    const axisColor = cssVar('--text-mute', '#8a8f98');
    const gridColor = cssVar('--border-faint', '#eceef1');
    const visible = series.filter((s) => !hidden[s.id]);
    const legend = legendActions(series, setHidden, focus);
    const legendSeries = series.map((s) => ({ id: s.id, name: s.name, color: colorById[s.id] }));

    return (
      <div className="linechart linechart--rc">
        <div className="linechart__plot">
          {visible.length === 0 ? allHiddenState(legend.onShowAll, focus.emptyRef) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 10, right: 12, bottom: 4, left: 4 }} barCategoryGap="14%"
                onMouseLeave={() => setActiveKey(null)}>
                <CartesianGrid vertical={false} stroke={gridColor} />
                <XAxis dataKey="x" tickFormatter={byDates ? fmt.dateShort : undefined}
                  tickLine={false} axisLine={{ stroke: gridColor }} minTickGap={byDates ? 24 : 4}
                  tick={{ fontSize: 10, fill: axisColor }} />
                <YAxis tickFormatter={af} tickLine={false} axisLine={false} width={byDates ? 58 : 44}
                  tick={{ fontSize: 10, fill: axisColor }} />
                <Tooltip content={(p) => <StackTip {...p} vf={vf} lf={byDates ? fmt.date : undefined} activeKey={activeKey} unit={seriesUnit} />} {...CHART_TOOLTIP_PROPS}
                  allowEscapeViewBox={{ x: tooltipEscapeX, y: true }}
                  cursor={{ fill: cssVar('--surface', '#f2f3f7'), opacity: 0.5 }} />
                {visible.map((s) => (
                  <Bar key={s.id} dataKey={s.id} name={s.name} stackId="load"
                    fill={colorById[s.id]} isAnimationActive={false}
                    onMouseEnter={() => setActiveKey(s.id)} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <ChartLegend series={legendSeries} hidden={hidden} {...legend} focusRef={focus.legendRef} />
      </div>
    );
  }

  /* ── HeatmapGrid ── день недели × час: rows[weekday] = значения по часам; интенсивность
     ячейки = value/max (opacity акцента). CSS-сетка (не recharts). Наведение — кастомный тултип
     .chart-tip (как у прочих графиков) + подсветка ячейки, вместо нативного title. */
  function HeatmapGrid({ hours = [], weekdays = [], rows = [], max = 0, valueFormat, metricLabel }) {
    const vf = valueFormat || ((v) => new Intl.NumberFormat('ru-RU').format(Math.round(Number(v) || 0)));
    const [tip, setTip] = useState(null);   // {wd, i, day, hour, value, x, y}
    if (!hours.length || !rows.length) return <ChartEmpty />;
    const mx = max || 1;
    const onCell = (e, wd, i, v) => setTip({
      wd, i, day: weekdays[wd], hour: String(hours[i]).padStart(2, '0') + ':00',
      value: v, x: e.clientX, y: e.clientY,
    });
    return (
      <div className="ov-heatmap-wrap">
        <div className="ov-heatmap" style={{ '--hm-cols': hours.length }} onMouseLeave={() => setTip(null)}>
          <div className="ov-heatmap__row ov-heatmap__row--head">
            <span className="ov-heatmap__wd" />
            {hours.map((h) => <span className="ov-heatmap__hh" key={h}>{String(h).padStart(2, '0')}</span>)}
          </div>
          {rows.map((row, wd) => (
            <div className="ov-heatmap__row" key={wd}>
              <span className="ov-heatmap__wd">{weekdays[wd]}</span>
              {row.map((v, i) => (
                /* is-empty — продаж не было (или данных нет): при наведении такая
                   ячейка не заливается акцентом целиком, иначе «нет продаж» в
                   подсказке стояло бы над самой «горячей» с виду клеткой */
                <span key={i}
                  className={'ov-heatmap__cell' + (v > 0 ? '' : ' is-empty')
                    + (tip && tip.wd === wd && tip.i === i ? ' is-hover' : '')}
                  onMouseEnter={(e) => onCell(e, wd, i, v)}
                  onMouseMove={(e) => onCell(e, wd, i, v)}
                  style={{ opacity: v > 0 ? (0.1 + 0.9 * (v / mx)) : 0.05 }} />
              ))}
            </div>
          ))}
        </div>
        {tip && (
          <div className="chart-tip heatmap-tip" role="tooltip"
            style={{ position: 'fixed', left: tip.x, top: tip.y - 12, transform: 'translate(-50%, -100%)', pointerEvents: 'none', zIndex: 40 }}>
            <div className="chart-tip__date">{tip.day} · {tip.hour}</div>
            <div className="chart-tip__row">
              {/* Ноль — это «продаж не было», и словами это читается однозначнее, чем
                  «0» рядом с ячейками по «0,3». Пустая ячейка (null) — «нет данных»:
                  неизвестное не выдаём за ноль. */}
              {tip.value == null || tip.value === 0 ? (
                <span className="chart-tip__name">{tip.value == null ? 'нет данных' : 'нет продаж'}</span>
              ) : (
                <>
                  <span className="chart-tip__name">{metricLabel || 'Загрузка'}</span>
                  <span className="chart-tip__val num">{vf(tip.value)}</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ── Sparkline ── points:[{date,value}] (или values:[number]) — мини-тренд KPI-карточки.
     viewBox 100×28 + preserveAspectRatio=none (тянется по ширине); штрих постоянной толщины
     через vector-effect=non-scaling-stroke. При наведении — дата+значение ближайшей точки. */
  function Sparkline({ points = [], values, tone, ariaLabel, valueFormat }) {
    const pts = points.length ? points : (values || []).map((v) => ({ value: v }));
    const ref = useRef(null);
    const [hi, setHi] = useState(null);
    const nums = pts.map((p) => p.value).filter((v) => v != null && Number.isFinite(v));
    if (nums.length < 2) return <span className="spark spark--empty" aria-hidden="true" />;
    const lo = Math.min(...nums);
    const hiV = Math.max(...nums);
    const span = (hiV - lo) || 1;
    const n = pts.length;
    const W = 100;
    const H = 28;
    const pad = 3;
    const xAt = (i) => pad + (n <= 1 ? 0 : (i / (n - 1)) * (W - pad * 2));
    const yAt = (v) => pad + (H - pad * 2) * (1 - (v - lo) / span);
    const coords = [];
    let lastI = -1;
    pts.forEach((p, i) => {
      if (p.value != null && Number.isFinite(p.value)) { coords.push(xAt(i) + ',' + yAt(p.value)); lastI = i; }
    });
    const color = tone ? `var(--status-${tone})` : 'var(--accent)';
    const vf = valueFormat || ((v) => fmt.money(v));
    const onMove = (e) => {
      const el = ref.current;
      if (!el || n < 2) return;
      const r = el.getBoundingClientRect();
      const idx = Math.max(0, Math.min(n - 1, Math.round(((e.clientX - r.left) / (r.width || 1)) * (n - 1))));
      setHi(idx);
    };
    const hov = hi != null ? pts[hi] : null;
    const hovOk = hov && hov.value != null && Number.isFinite(hov.value);
    // Точки рисуем HTML-кружками с %-позицией (а не SVG <circle>): при preserveAspectRatio=none
    // svg растянут по X → <circle> стал бы эллипсом. % внутри обёртки = тот же viewBox 100×28.
    const dotPos = (i) => ({ left: `${xAt(i)}%`, top: `${(yAt(pts[i].value) / H) * 100}%` });
    return (
      <div className="spark-wrap" ref={ref} onMouseMove={onMove} onMouseLeave={() => setHi(null)}>
        <svg className="spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={ariaLabel || 'тренд'}>
          <polyline points={coords.join(' ')} className="spark__line" style={{ stroke: color }} vectorEffect="non-scaling-stroke" />
        </svg>
        {lastI >= 0 && <span className="spark__pt" style={{ ...dotPos(lastI), background: color }} />}
        {hovOk && <span className="spark__pt spark__pt--cur" style={{ ...dotPos(hi), background: color }} />}
        {hovOk && (
          <div className="spark-tip" style={{ left: `${xAt(hi)}%` }}>
            {hov.date && <span className="spark-tip__date">{fmt.dateShort(hov.date)}</span>}
            <span className="spark-tip__val num">{vf(hov.value)}</span>
          </div>
        )}
      </div>
    );
  }

  /* ── DonutChart (Recharts PieChart) ── data:[{label,value,color?,tone?}] ── кольцо + своя
     ₽/%-легенда. Ховер сектора/легенды: затемняет остальные + РИДАУТ в центре кольца
     (clip-proof — плавающую плашку на маленьком донате всегда режет). Цвета из токенов.
     onPick(item) — опционально: клик по сектору/строке легенды (деталка разреза).
     legendUnit — подпись числа на развороте легенды: строка или (n) => строка.

     Легенда — ExpandableList: первые десять строк, остальные — одной строкой-итогом
     «Ещё N · сумма · доля» и разворотом «Показать все — N». Структура выручки у сети
     — это тридцать с лишним категорий, и полная легенда вытягивала блок на экран
     вниз ради строк по 0,3 %. Кольцо по-прежнему рисует ВСЕ доли: сворачивается
     только подпись. Сумма видимых строк и итога равна итогу кольца — свёрнутый
     список не теряет денег. Малые легенды (до 12 строк) не сворачиваются вовсе. */
  const DONUT_LEGEND_LIMIT = EXPAND_LIMIT;
  const legendUnitDefault = (n) => fmt.plural(n, 'строка', 'строки', 'строк');

  function DonutChart({ data = [], size = 132, thickness = 18, valueFormat, showLegend = true, onPick, legendUnit }) {
    const vf = valueFormat || ((v) => fmt.money(v));
    // hi — индекс подсвеченной доли; 'rest' — все доли под строкой-итогом разом
    const [hi, setHi] = useState(null);
    const palette = seriesPalette();
    const items = (data || []).filter((d) => d && d.value > 0).map((d, i) => ({
      ...d,
      __color: d.color || (d.tone ? cssVar(`--status-${d.tone}`, palette[i % palette.length]) : palette[i % palette.length]),
    }));
    const total = items.reduce((s, d) => s + d.value, 0);
    if (!total) return <ChartEmpty />;
    const pct = (v) => fmt.percent((v / total) * 100, { digits: 1 });
    const outer = size / 2 - 1;
    const inner = Math.max(0, outer - thickness);
    const restHi = hi === 'rest';
    const lit = (i) => (hi == null || (restHi ? i >= DONUT_LEGEND_LIMIT : hi === i));
    let hov = null;
    if (restHi) {
      const rest = items.slice(DONUT_LEGEND_LIMIT);
      hov = { label: 'Ещё ' + rest.length, value: rest.reduce((s, d) => s + d.value, 0) };
    } else if (hi != null) hov = items[hi];
    const pickKey = (d) => (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(d); } };
    return (
      <div className="donut">
        <div className="donut__ring" style={{ width: size, height: size }}>
          <PieChart width={size} height={size}>
            <Pie data={items} dataKey="value" nameKey="label" cx="50%" cy="50%"
              innerRadius={inner} outerRadius={outer} startAngle={90} endAngle={-270}
              stroke="none" paddingAngle={items.length > 1 ? 1 : 0} isAnimationActive={false}
              style={onPick ? { cursor: 'pointer' } : undefined}
              onClick={onPick ? (_, i) => onPick(items[i]) : undefined}
              onMouseEnter={(_, i) => setHi(i)} onMouseLeave={() => setHi(null)}>
              {items.map((d, i) => (
                <Cell key={i} fill={d.__color} opacity={lit(i) ? 1 : 0.4} />
              ))}
            </Pie>
          </PieChart>
          {hov && (
            <div className="donut__center" aria-hidden="true">
              <span className="donut__center-share num">{pct(hov.value)}</span>
              <span className="donut__center-lbl">{hov.label}</span>
            </div>
          )}
        </div>
        {showLegend && (
          /* Обёртка держит место легенды в раскладке доната (все прежние правила
             `.donut__legend` — ширина, колонка на узком экране — относятся к ней), а
             внутри — сам список и кнопка разворота ПОД ним. */
          <div className={'donut__legend' + (onPick ? ' donut__legend--click' : '')}>
            <ExpandableList items={items} limit={DONUT_LEGEND_LIMIT} as="ul" listClassName="donut__legend-list"
              unit={legendUnit || legendUnitDefault}
              render={(d, i) => (
                <li className={'donut__leg' + (hi === i ? ' is-hi' : '')} key={i}
                  onMouseEnter={() => setHi(i)} onMouseLeave={() => setHi(null)}
                  onFocus={onPick ? () => setHi(i) : undefined} onBlur={onPick ? () => setHi(null) : undefined}
                  onClick={onPick ? () => onPick(d) : undefined}
                  role={onPick ? 'button' : undefined} tabIndex={onPick ? 0 : undefined}
                  title={onPick ? 'Открыть детали: ' + d.label : undefined}
                  onKeyDown={onPick ? pickKey(d) : undefined}>
                  <span className="donut__leg-dot" style={{ background: d.__color }} />
                  <span className="donut__leg-name truncate" title={d.label}>{d.label}</span>
                  <span className="donut__leg-val num">{vf(d.value)}</span>
                  <span className="donut__leg-share num">{pct(d.value)}</span>
                </li>
              )}
              summary={(rest) => {
                const sum = rest.reduce((s, d) => s + d.value, 0);
                return (
                  <li className={'donut__leg donut__leg--rest' + (restHi ? ' is-hi' : '')} key="__rest"
                    onMouseEnter={() => setHi('rest')} onMouseLeave={() => setHi(null)}>
                    <span className="donut__leg-dot donut__leg-dot--rest" />
                    <span className="donut__leg-name truncate">Ещё {rest.length}</span>
                    <span className="donut__leg-val num">{vf(sum)}</span>
                    <span className="donut__leg-share num">{pct(sum)}</span>
                  </li>
                );
              }} />
          </div>
        )}
      </div>
    );
  }

  /* ── ChartEmpty ── icon/action — для состояний, где есть что сделать
     («все элементы графика скрыты» → «Показать все»). */
  function ChartEmpty({ label = 'Нет данных за период', icon = 'analytics', action }) {
    return (
      <div className="chart-empty">
        <Icon name={icon} size={22} className="text-mute" />
        <span className="t-body-sm text-mute">{label}</span>
        {action}
      </div>
    );
  }

/* Графики отдаются в React.memo.

   Замер («Закупки», ws8035): клик по заголовку столбца таблицы морозил интерфейс
   на 658 мс — при 110 строках. Дело не в строках: сортировка меняет состояние
   отчёта, отчёт перерисовывается целиком, и вместе с ним заново рисуются
   стек-бары по дням и донат — при том, что их данные не изменились ни на байт.

   Пропсы графиков — это уже мемоизированные в отчётах массивы (`trendSeries`,
   `structData` и т.п.), поэтому поверхностное сравнение здесь работает: у
   неизменных данных ссылка та же, и перерисовка пропускается. Компоненты чистые,
   от контекста не зависят — брать во внимание нечего, кроме пропсов.

   ⚠️ Если график когда-нибудь начнёт принимать объект, собираемый прямо в JSX
   (`data={{...}}`), memo перестанет спасать молча: ссылка будет новой на каждый
   рендер. Такие пропсы держать мемоизированными на стороне отчёта.

   ТЕМА. Recharts красит SVG-атрибутами, `var()` в них не работает, поэтому цвета
   осей, сетки и курсора резолвятся из CSS-переменных ПРИ ОТРИСОВКЕ (см. cssVar).
   Значит memo ломает обещание «тема учитывается»: при смене темы пропсы те же,
   перерисовки нет, цвета остаются от прежней. В тёмной теме переопределены
   `--text-mute`, `--border`, `--text-dim`, `--bg-elev` — то есть ось и курсор
   поехали бы молча.

   Переключателя темы в приложении сегодня нет (data-theme ставит только витрина
   /dev/uikit), но оставлять это правилом в комментарии нельзя: комментарий не
   выполняется. Поэтому тема передаётся графику ОТДЕЛЬНЫМ пропом — меняется
   атрибут, меняется проп, memo пропускает перерисовку. */
function useThemeToken() {
  const [t, setT] = React.useState(() => (typeof document === 'undefined' ? ''
    : document.documentElement.getAttribute('data-theme') || ''));
  React.useEffect(() => {
    if (typeof MutationObserver === 'undefined') return undefined;
    const root = document.documentElement;
    const mo = new MutationObserver(() => setT(root.getAttribute('data-theme') || ''));
    mo.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => mo.disconnect();
  }, []);
  return t;
}

const memoized = (C) => {
  const M = React.memo(C);
  // Проп `theme` графики не читают — он нужен ровно для того, чтобы memo увидел
  // изменение. Внутрь DOM не попадает: ни один график не разливает пропсы.
  const ThemeAware = (props) => <M {...props} theme={useThemeToken()} />;
  ThemeAware.displayName = (C.displayName || C.name || 'Chart') + 'Memo';
  return ThemeAware;
};

const HBarChartMemo = memoized(HBarChart);
const LineChartMultiMemo = memoized(LineChartMulti);
const StackedBarChartMemo = memoized(StackedBarChart);
const HeatmapGridMemo = memoized(HeatmapGrid);
const SparklineMemo = memoized(Sparkline);
const DonutChartMemo = memoized(DonutChart);

export { HBarChartMemo as HBarChart };
export { LineChartMultiMemo as LineChartMulti };
export { StackedBarChartMemo as StackedBarChart };
export { HeatmapGridMemo as HeatmapGrid };
export { SparklineMemo as Sparkline };
export { DonutChartMemo as DonutChart };
export { ChartEmpty };
export { LcTip };
export { StackTip, tipRows, TIP_MAX };
// палитра серий — для теста «10 серий — 10 разных цветов» (seriesPalette.test.js)
export { SERIES_TOKENS, seriesPalette, seriesColors };
